import {
  getValidatedRequestContext,
  type RequestContext,
  type RequestContextValidationOptions,
  type ValidatedRequestContext,
} from '@studio/utils/requestContext';
import type { Super } from '@studio/utils/types';

export interface RequestOptions extends RequestContextValidationOptions {
  readonly name?: string;
}

export default function withOptions<
  const Options extends RequestOptions,
  const Args extends readonly unknown[],
  const ReturnType,
>(
  options: Super<Options, RequestOptions>,
  handler: (
    context: ValidatedRequestContext<Options>,
    ...args: Args
  ) => ReturnType | PromiseLike<ReturnType>,
): {
  (context: RequestContext, ...args: Args): Promise<ReturnType>;
  readonly options: Options;
} {
  const wrappedHandlerName = options.name ?? handler.name;

  // This is a trick to give the function a specific name
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const wrappedHandler = {
    async [wrappedHandlerName](
      context: RequestContext,
      ...args: Args
    ): Promise<ReturnType> {
      return await handler(
        getValidatedRequestContext(context, options),
        ...args,
      );
    },
  }[wrappedHandlerName]!;

  return Object.assign(wrappedHandler, {
    options: options as Options,
  });
}
